import { floorBandRoomForPosition, type FloorBandBox } from '../../encounter-creation-engine/floor-band.js';
import { boxIsRoofed } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { TEMPERATURE_BAND_LIST } from '../../shared/env-types.js';
import { encounterWaterModel, submersionAt, type WaterModel } from './traversal-3d.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const id = (v: unknown): string => {
  while (Array.isArray(v)) v = v[0];
  const shell = record(v);
  if (shell) return id(shell.items ?? shell.item_id);
  return String(v ?? '').toUpperCase();
};
export { id as climateItemId };
export const FRIGID_ID = '407B4AC5-27C4-60AD-FD56-9189FCDC8AE6';
export const CHILL_ID = 'EE3151A4-5BA0-52EB-85C8-B4E829E888BF';
export const ARCTIC_ID = 'D0E5D185-0E49-FBE3-EFA9-E99CADDE7B0C';
export const SNOW_GROUND_ID = '80BD5C08-9661-664B-D51F-0A6C4FBD0FDE';
export const ICE_SURFACE_ID = '8110921E-DED4-9A70-C9F5-113B0ACFC443';
const indoors = new Set(['38FB8933-47F6-4A79-60CF-14E80BCF9BF5', '19BCE18F-E1DC-D359-48A0-3B789741E058']);

export function climateEncounterField(encounter: Row, key: string): unknown {
  return record(encounter.encounters)?.[key] ?? record(encounter.encounter_creatures)?.[key] ?? encounter[key];
}
export function encounterClimateShape(encounter: Row): unknown {
  const value = climateEncounterField(encounter, 'encounter_shape');
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value) as unknown; } catch { return null; }
}
/** Physical membership, including floor contact preference and the ceiling exclusion used by room ambience. */
export function climateRoomAt(shape: unknown, x: number, y: number, z: number): (FloorBandBox & Row) | null {
  return floorBandRoomForPosition(climateRooms(shape), x, y, z);
}
function climateRooms(shape: unknown): (FloorBandBox & Row)[] {
  const boxes = record(shape)?.boxes;
  return Array.isArray(boxes) ? boxes.filter(b => record(b) && !b.roofDeck && !b.wallTop && !b.ridge
    && record(b.x) && record(b.y)) as (FloorBandBox & Row)[] : [];
}
type ClimateRoom = FloorBandBox & Row;
function climateRoomIndex(rooms: readonly ClimateRoom[]): Map<string, ClimateRoom> {
  return new Map(rooms.map(room => [String(room.id ?? ''), room]));
}
/** Climate is shared by a place; physical membership and roof checks keep the member box. */
function climatePrimaryRoom(room: ClimateRoom | null, byId: ReadonlyMap<string, ClimateRoom>): ClimateRoom | null {
  for (let remaining = byId.size; room && remaining > 0; remaining--) {
    const targetId = record(room.creation)?.mergedInto;
    if (typeof targetId !== 'string' || !targetId) break;
    const target = byId.get(targetId);
    if (!target) break;
    room = target;
  }
  return room;
}
function roomTemperature(room: ClimateRoom | null, inherited: string | null, byId: ReadonlyMap<string, ClimateRoom>): string | null {
  const primary = climatePrimaryRoom(room, byId);
  const override = id(record(record(primary?.creation)?.climate)?.temperature);
  return TEMPERATURE_BAND_LIST.some(b => b.item_id === override) ? override : inherited;
}
export function effectiveTemperatureBandAt(encounter: Row, shape: unknown, x: number, y: number, z: number): string | null {
  const rooms = climateRooms(shape);
  const room = floorBandRoomForPosition(rooms, x, y, z);
  const inherited = id(climateEncounterField(encounter, 'encounter_tempurature') ?? climateEncounterField(encounter, 'encounter_temperature'));
  return roomTemperature(room, inherited || null, climateRoomIndex(rooms));
}
export type GroundCondition = 'none' | 'snow' | 'ice';
export interface ClimateGroundContext {
  encounter: Row; shape: unknown; weather: Row | null;
  rooms: ClimateRoom[]; roomsById: ReadonlyMap<string, ClimateRoom>;
  temperature: string | null; environment: string; ground: string;
  water: WaterModel | null; active: boolean;
}
/** Immutable inputs for one move/route query. Rebuild after scene or catalog changes. */
export function prepareClimateGround(encounter: Row, shape: unknown, weather: Row | null = null): ClimateGroundContext {
  const rooms = climateRooms(shape);
  const roomsById = climateRoomIndex(rooms);
  const temperature = id(climateEncounterField(encounter, 'encounter_tempurature') ?? climateEncounterField(encounter, 'encounter_temperature')) || null;
  const environment = id(climateEncounterField(encounter, 'encounter_environment'));
  const currentWeather = climateEncounterField(encounter, 'encounter_weather');
  const donor = weather ?? record(record(encounter.catalog)?.[id(currentWeather)]) ?? record(currentWeather);
  const ground = id(record(donor?.item_layer_types)?.ground_terrain_type ?? donor?.ground_terrain_type);
  const water = encounterWaterModel({ encounters: { encounter_water_level: climateEncounterField(encounter, 'encounter_water_level'),
    encounter_shape: shape } });
  const bands = [temperature, ...rooms.map(room => roomTemperature(room, temperature, roomsById))];
  const active = (bands.includes(FRIGID_ID) && water !== null)
    || (bands.some(b => b === FRIGID_ID || b === CHILL_ID) && !indoors.has(environment)
      && (environment === ARCTIC_ID || ground === SNOW_GROUND_ID || ground === ICE_SURFACE_ID));
  return { encounter, shape, weather, rooms, roomsById, temperature, environment, ground, water, active };
}
export function climateGroundAt(context: ClimateGroundContext, x: number, y: number, z: number): GroundCondition {
  if (!context.active) return 'none';
  const room = floorBandRoomForPosition(context.rooms, x, y, z);
  const band = roomTemperature(room, context.temperature, context.roomsById);
  if (band === FRIGID_ID && context.water && submersionAt(context.water, x, y, z) > 0) return 'ice';
  if (band !== FRIGID_ID && band !== CHILL_ID) return 'none';
  if (indoors.has(context.environment) || boxIsRoofed(room)) return 'none';
  if (context.ground === ICE_SURFACE_ID) return 'ice';
  return context.environment === ARCTIC_ID || context.ground === SNOW_GROUND_ID ? 'snow' : 'none';
}
/** Weather is the current catalog definition, never a name-based guess. Water is read before mover exemptions. */
export function groundConditionAt(encounter: Row, shape: unknown, x: number, y: number, z: number, weather: Row | null = null): GroundCondition {
  return climateGroundAt(prepareClimateGround(encounter, shape, weather), x, y, z);
}
